import { toPng } from 'html-to-image';

export const statusIsGood = status => status >= 200 && status < 300;
export const statusIsUnauthorized = status => status === 401;

export const stringIsEmptyOrNull = str => str == null || str === '';

export const getSlugFromName = str => (str ? str.toLowerCase().split(' ').join('_') : null);

export const camelCaseToKebabCase = str => {
  return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
};

export const makePrettyFromCamelCase = str => {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

export const getLocalStorageItem = key => {
  try {
    const result = localStorage.getItem(key);
    return result;
  } catch {
    return null;
  }
};

export const setLocalStorageItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // since we only use this as a backup to in-memory state, don't need additional action on error
  }
};

// Connects two items by "&", and 3+ items by "__, __, ... __, & __"
export const prettyEnglishList = (strList, allLowerCase) => {
  if (!strList || strList.length === 0) {
    return null;
  }
  if (strList.length === 1) {
    return strList[0];
  }
  // make a new list where all but the first item are converted to lowercase
  const newList = strList.map((item, idx) => {
    if (idx === 0 && !allLowerCase) {
      return item;
    }
    return item.toLowerCase();
  });
  const last = newList.pop();
  return newList.join(', ') + ' & ' + last;
};

export const getRandomInt = max => Math.floor(Math.random() * max);

export const makeRandomId = length => {
  var result = '';
  var characters = '0123456789';
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const capitalizeFirstLetter = word => {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : '';
};

export const getBoundsParams = filterInfo => {
  const bounds = filterInfo.bounds;
  const minLon = bounds[0][1];
  const minLat = bounds[0][0];
  const maxLon = bounds[1][1];
  const maxLat = bounds[1][0];
  return `xmin=${minLon}&ymin=${minLat}&xmax=${maxLon}&ymax=${maxLat}`;
};

export const formatDate = dateString => {
  // Create a new Date object from the input string
  const date = new Date(dateString);

  // Extract month, day, and year
  const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() is zero-based
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  // Return formatted date
  return `${month}/${day}/${year}`;
};

export const numberToCurrency = amount => {
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return currencyFormatter.format(amount);
};

export const componentToHex = c => {
  var hex = c.toString(16);
  return hex.length === 1 ? '0' + hex : hex;
};

export const rgbToHex = ([r, g, b]) => {
  return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
};

// This is chart specific--if we will need to refactor this function for other uses
export const exportChartAsPNG = async (chartOptions, filterInfo, tableRef, hazard, chartName) => {
  if (!tableRef.current) return;

  const format = chartOptions.type || 'PNG';
  const filename = `${filterInfo.areaName}-${filterInfo.planningAreaLevel}-extent-chart.${format.toLowerCase()}`;

  try {
    const dataUrl = await toPng(tableRef.current, {
      cacheBust: true,
      backgroundColor: '#ffffff',
      pixelRatio: 2,
    });

    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    link.click();
  } catch (err) {
    console.error('Failed to export table as PNG:', err);
  }
};

// This is chart specific--if we will need to refactor this function for other uses
export const exportChartAsCSV = (filterInfo, columns, data, hazard, chartName) => {
  if (!data || !data.length) return;

  const filename = `${filterInfo.areaName}-${filterInfo.planningAreaLevel}-${chartName}-${hazard}.csv`;

  const headers = columns.map(col => `"${col.name}"`).join(',');

  const rows = data
    .map(row => {
      return columns
        .map(col => {
          let value;

          if (col.key === 'begin_date') {
            value = formatDate(row.begin_date);
          } else if (col.key === 'eventType') {
            value = capitalizeFirstLetter(row.hazard);
          } else if (col.key === 'propDamage') {
            value = row.damage_property_adj24?.toFixed(2) || 0;
          } else if (col.key === 'cropDamage') {
            value = row.damage_crops_adj24?.toFixed(2) || 0;
          } else {
            value = row[col.key] || '';
          }

          const stringValue = String(value);
          if (
            stringValue.includes(',') ||
            stringValue.includes('"') ||
            stringValue.includes('\n')
          ) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        })
        .join(',');
    })
    .join('\n');

  const csvContent = `${headers}\n${rows}`;

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const downloadGeojsonAsText = (geojsonData, fileName) => {
  if (!geojsonData) return;

  const stringToDownload = JSON.stringify(geojsonData);

  const blob = new Blob([stringToDownload], { type: 'application/geo+json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
